<template>
  <div class="h-0 steps-indicator" v-if="sections.length > 0">
    <div v-for="(section, i) in sections" :key="section.id" class="dot-box" @click="onClickStep(i)">
      <span
        v-tooltip="{ text: section.title, theme: { placement: 'right', 'font-size': '1rem' } }"
        class="numbered-dot data-v-tooltip"
        :class="{
          active: currentActiveStep === i,
        }">
        <span>{{ i + 1 }}</span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { getWidgetScrollContainer, scrollToElement } from '@/helpers/shared'
import i18n from '@/i18n'

interface Section {
  id: string
  title: string
}

const sections = ref<Section[]>([])
const currentActiveStep = ref(0)

function sectionsAreEqual(left: Section[], right: Section[]): boolean {
  if (left.length !== right.length) return false
  return left.every((section, index) => {
    const candidate = right[index]
    return section.id === candidate?.id && section.title === candidate?.title
  })
}

function scanSections(): void {
  // Find all sections with class core-container (BaseSection component)
  const elements = document.querySelectorAll('section.core-container')
  const newSections: Section[] = []

  elements.forEach((el, index) => {
    // Extract title from first h2 element
    const h2 = el.querySelector('h2')
    const title = h2?.textContent?.trim() || ''

    // Use element's id or generate one
    const id = el.id || `section-${index}`

    newSections.push({ id, title })
  })

  if (!sectionsAreEqual(sections.value, newSections)) {
    sections.value = newSections
  }
}

// Batch scroll work to animation frames and avoid repeated state writes.
let scrollRafId: number | null = null
let scrollTarget: Window | HTMLElement | null = null
let scanRafId: number | null = null

function scheduleScanSections(): void {
  if (scanRafId !== null) return
  scanRafId = window.requestAnimationFrame(() => {
    scanRafId = null
    scanSections()
  })
}

function onScroll(): void {
  if (scrollRafId !== null) return

  scrollRafId = window.requestAnimationFrame(() => {
    scrollRafId = null

    const elements = Array.from(document.querySelectorAll<HTMLElement>('section.core-container'))
    if (elements.length === 0) return

    const viewportMiddle =
      scrollTarget instanceof HTMLElement
        ? scrollTarget.getBoundingClientRect().top + scrollTarget.clientHeight / 2
        : window.innerHeight / 2

    let nextActiveStep = 0
    for (const [index, el] of elements.entries()) {
      if (el.getBoundingClientRect().top < viewportMiddle) {
        nextActiveStep = index
        continue
      }
      break
    }

    if (nextActiveStep !== currentActiveStep.value) {
      currentActiveStep.value = nextActiveStep
    }
  })
}

function onClickStep(index: number): void {
  const elements = document.querySelectorAll('section.core-container')
  const element = elements[index]
  if (element) {
    scrollToElement(element)
  }
}

// Use MutationObserver to detect structural and title text changes
let observer: MutationObserver | null = null

onMounted(async () => {
  // Initial scan after DOM is ready
  await nextTick()
  scanSections()

  // Listen for scroll
  scrollTarget = getWidgetScrollContainer() ?? window
  scrollTarget.addEventListener('scroll', onScroll, { passive: true })
  onScroll()

  observer = new MutationObserver(() => {
    scheduleScanSections()
  })

  // Observe the parent container for child changes
  const wrapper = document.querySelector('.core-wrapper')
  if (wrapper) {
    observer.observe(wrapper, { childList: true, characterData: true, subtree: true })
  }
})

watch(
  () => i18n.global.locale.value,
  () => {
    void nextTick(() => scheduleScanSections())
  }
)

onUnmounted(() => {
  scrollTarget?.removeEventListener('scroll', onScroll)
  observer?.disconnect()
  if (scrollRafId !== null) {
    window.cancelAnimationFrame(scrollRafId)
  }
  if (scanRafId !== null) {
    window.cancelAnimationFrame(scanRafId)
  }
})
</script>

<style lang="postcss" scoped>
.steps-indicator {
  @apply sticky top-24 hidden flex-col xl:inline-flex;
  @apply z-20 lg:-ml-12 xl:-ml-20 2xl:-ml-24;
  top: calc(var(--sticky-top-offset, var(--header-height)) + 2rem);
  &.no-header {
    top: 2rem;
  }

  .dot-box {
    @apply pr-2 lg:pr-0;
    @apply cursor-pointer;
    .numbered-dot {
      @apply inline-block rounded-full text-sm font-bold;
      @apply text-center bg-neutral-600 text-white;
      @apply mb-4 h-7 w-7;
      @apply flex items-center justify-center;
      transition: background-color 0.3s;
      &.active {
        @apply bg-primary-default text-white;
      }
    }
  }
}
.core-tab-bar + .core-wrapper .steps-indicator {
  top: calc(var(--sticky-top-offset, var(--header-height)) + 2rem);
}
</style>
